import React, { useState, useEffect } from "react";
import { FaTimesCircle, FaCheckCircle, FaTrashAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import type { U2000SavedCriteria } from "../../basic/U2000BasicSearch";
import type { U2000SearchStateHandlers } from "../../../../reducer/formReducer/U2000SearchStateHandlers";

export interface U2000SearchFilterManagmentProps {
  searchStateHandlers: U2000SearchStateHandlers;
  open: boolean;
  onFilterUpdate?: (id: string, filterName: string, isDefaultFilter: boolean, isFromFilterManagement: boolean) => void;
  onFilterDelete?: (id: string) => void;
  onClose: () => void;
}

const U2000SearchFilterManagment: React.FC<U2000SearchFilterManagmentProps> = ({ searchStateHandlers, open, onClose, onFilterUpdate, onFilterDelete }) => {
  const { t } = useTranslation();
  const [editState, setEditState] = useState<{ [id: string]: { name: string; isDefaultFilter: boolean } }>({});

  // Synchronize editState with criteriasState when dialog opens or criteriasState changes
  useEffect(() => {
    if (open) {
      const initial: { [id: string]: { name: string; isDefaultFilter: boolean } } = {};
      searchStateHandlers.getSavedFilters().forEach((c) => {
        initial[c.id] = { name: c.name, isDefaultFilter: !!c.isDefaultFilter };
      });
      setEditState(initial);
    }
    if (!open) {
      setEditState({});
    }
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 flex justify-end z-50">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/30 transition-opacity" onClick={onClose} aria-label="close dialog overlay"></div>
      {/* Dialog */}
      <div className="relative w-full max-w-md bg-gray-200 px-3 py-6 rounded-md shadow-lg z-50">
        <button
          className="absolute right-3 top-3 flex items-center justify-center h-8 w-8 text-gray-500 hover:text-gray-700
                   rounded-lg transition-all duration-200 ease-in-out
                   hover:bg-gray-100 focus:bg-gray-100
                   focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
          onClick={onClose}
          aria-label="Fermer la boîte de dialogue"
        >
          <span className="text-lg">✕</span>
        </button>
        <h3 className="mb-6 border-b border-gray-200 pb-4 text-lg font-semibold text-gray-900">{t("dlgCriteriasManageFilterTitle")}</h3>
        <p className="mb-4 text-sm text-gray-600 italic">{t("dlgCriteriasIsDefaultHint")}</p>
        {searchStateHandlers.getSavedFilters().length > 0 &&
          searchStateHandlers
            .getSavedFilters()
            .sort((a: U2000SavedCriteria, b: U2000SavedCriteria) => (b.isDefaultFilter ? 1 : 0) - (a.isDefaultFilter ? 1 : 0))
            .map((criteria: U2000SavedCriteria) => {
              const local = editState[criteria.id] || { name: criteria.name, isDefaultFilter: !!criteria.isDefaultFilter };
              const isModified = local.name !== criteria.name || local.isDefaultFilter !== !!criteria.isDefaultFilter;
              return (
                <div
                  key={criteria.id + "A"}
                  className={`mb-4 p-4 border border-gray-200 rounded-lg bg-white shadow-xs
                            hover:shadow-md transition-shadow duration-200
                            ${criteria.isDefaultFilter ? "border-blue-300 bg-blue-50" : ""}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center">
                      <input
                        key={criteria.id + "B"}
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded 
                                 focus:ring-2 focus:ring-blue-500/20 focus:outline-hidden
                                 transition-colors duration-150"
                        checked={local.isDefaultFilter}
                        onChange={(e) => setEditState((s) => ({ ...s, [criteria.id]: { ...local, isDefaultFilter: e.target.checked } }))}
                      />
                      <label className="ml-2 text-sm text-gray-700 font-medium">Par défaut</label>
                    </div>
                    <input
                      key={criteria.id + "C"}
                      type="text"
                      className="flex-1 px-1 py-2 border border-gray-300 rounded-lg text-sm
                               focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400
                               transition-all duration-200
                               placeholder:text-gray-400"
                      value={local.name}
                      placeholder="Nom du filtre"
                      onChange={(e) => {
                        setEditState((s) => ({ ...s, [criteria.id]: { ...local, name: e.target.value } }));
                      }}
                    />
                    <div className="flex items-center">
                      <button
                        key={criteria.id + "U"}
                        className={`flex items-center justify-center h-8 w-8 rounded-lg transition-all duration-200 ease-in-out
                                  focus:outline-hidden focus:ring-2 focus:ring-green-500/20
                                  ${
                                    !isModified || !local.name || local.name.trim() === "" ? "text-gray-300 cursor-not-allowed" : "text-green-600 hover:text-white hover:bg-green-600 active:scale-95"
                                  }`}
                        title="Mettre à jour"
                        onClick={() => {
                          onFilterUpdate?.(criteria.id, local.name, local.isDefaultFilter, true);

                          // mettre à jour l'état isDefaultFilter a tout les autres critères au cas ou celui ci est mis par défaut
                          // seulement si local.isDefaultFilter est true
                          if (local.isDefaultFilter) {
                            setEditState((prev) => {
                              const updated = { ...prev };
                              Object.entries(updated).forEach(([id, value]) => {
                                updated[id] = { ...value, isDefaultFilter: id === criteria.id };
                              });
                              return updated;
                            });
                          }
                        }}
                        disabled={!isModified || !local.name || local.name.trim() === ""}
                      >
                        <FaCheckCircle className="text-sm" />
                      </button>
                      <button
                        key={criteria.id + "C"}
                        className={`flex items-center justify-center h-8 w-8 rounded-lg transition-all duration-200 ease-in-out
                                  focus:outline-hidden focus:ring-2 focus:ring-gray-500/20
                                  ${!isModified ? "text-gray-300 cursor-not-allowed" : "text-gray-500 hover:text-white hover:bg-gray-500 active:scale-95"}`}
                        title="Annuler"
                        onClick={() => setEditState((s) => ({ ...s, [criteria.id]: { name: criteria.name, isDefaultFilter: !!criteria.isDefaultFilter } }))}
                        disabled={!isModified}
                      >
                        <FaTimesCircle className="text-sm" />
                      </button>
                      <button
                        key={criteria.id + "D"}
                        className={`flex items-center justify-center h-8 w-8 rounded-lg transition-all duration-200 ease-in-out
                                  focus:outline-hidden focus:ring-2 focus:ring-red-500/20
                                  ${isModified ? "text-gray-300 cursor-not-allowed" : "text-red-500 hover:text-white hover:bg-red-500 active:scale-95"}`}
                        title="Supprimer"
                        onClick={() => onFilterDelete?.(criteria.id)}
                        disabled={isModified}
                      >
                        <FaTrashAlt className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
      </div>
    </div>
  );
};

export default U2000SearchFilterManagment;
